import {Button, Input, Modal} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Space} from 'src/design/atoms/Space';
import {Text} from 'src/design/atoms/Text';
import {renderFieldLabel} from 'src/app/forms/renderFieldLabel';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Textbox} from 'src/design/atoms/Textbox';
import {Paginator} from 'src/design/organisms/Pagination';
import {isValidArray} from 'src/utilities/objectUtils';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';

export interface FormSelectModalV2Props {
  field: IFormFieldV2;
  value: any;
  error?: string;
  onChange: (name: string, value: any, displayValue?: string) => void;
}

const maxPageRows = 7;

export const FormSelectModalV2 = React.memo<FormSelectModalV2Props>(
  ({field, value, error, onChange}: FormSelectModalV2Props) => {
    const rawModalData = useMemo(() => field.options || [], [field.options]);

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);
    const [showOnlySelected, setShowOnlySelected] = useState<boolean>(false);
    const [displayedData, setDisplayedData] = useState<ISelectOption[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasSelectedAll, setHasSelectedAll] = useState(false);

    const isSelected = useCallback(
      (item: ISelectOption): boolean => {
        return (
          selected.filter(element => element.toString() === item.id.toString())
            .length > 0
        );
      },
      [selected]
    );

    // Parse incoming value into array of IDs
    useMemo(() => {
      if (!value) {
        setSelected([]);
        return;
      }

      // Handle array of objects with id/label
      if (
        Array.isArray(value) &&
        value.length > 0 &&
        typeof value[0] === 'object'
      ) {
        const values = value.map(v => v.id.toString());
        setSelected(values);
        return;
      }

      // Handle array of primitive values
      if (Array.isArray(value)) {
        setSelected(value.map(v => v.toString()));
        return;
      }

      // Handle single primitive value
      setSelected([value.toString()]);
    }, [value]);

    useEffect(() => {
      if (!rawModalData) {
        return;
      }

      let results = Array.from(rawModalData);

      if (searchTerm) {
        const lowerSearch = searchTerm?.toLowerCase();
        results = results.filter(
          o => o.label.toLowerCase().includes(lowerSearch) === true
        );
      }

      if (showOnlySelected === true) {
        results = results.filter(o => isSelected(o));
      }

      setTotalRecords(results.length);

      const currPage = currentPage - 1;
      const startPageMin = currPage <= 0 ? 0 : currPage;

      const startIndex = startPageMin * maxPageRows;
      const endIndex = (startPageMin + 1) * maxPageRows;

      const pagedResult = results.slice(startIndex, endIndex);

      setDisplayedData(pagedResult);
    }, [searchTerm, showOnlySelected, currentPage, rawModalData, selected]);

    const handleCloseDialog = useCallback(() => {
      setOpen(false);
    }, [setOpen]);

    const handleSelect = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
        const filtered = selected.filter(item => item !== id);
        if (e.target.checked) {
          setSelected([...filtered, id]);
        } else {
          setSelected(filtered);
        }
      },
      [selected, setSelected]
    );

    const handleSelectAll = useCallback(
      (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setHasSelectedAll(checked);

        if (checked) {
          setSelected(rawModalData.map(item => item.id.toString()));
        } else {
          setSelected([]);
        }
      },
      [rawModalData, setSelected]
    );

    const handleShowOnlySelected = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const {checked} = e.target;

        setCurrentPage(1);
        setShowOnlySelected(checked);
      },
      [setShowOnlySelected]
    );

    const handleSaveSelections = useCallback(() => {
      if (isValidArray(rawModalData)) {
        const selectedDataValues = rawModalData.filter(item =>
          selected.includes(item.id.toString())
        );

        onChange(field.name, selectedDataValues, getFormLabelText());
        setOpen(false);
      }
    }, [rawModalData, selected, field.name, onChange, setOpen]);

    const handlePageChange = useCallback(
      (event: React.ChangeEvent<unknown>, page: number): void =>
        setCurrentPage(page),
      [setCurrentPage]
    );

    const handleSearchTerm = useCallback(
      (e: any) => {
        const {value} = e.target;
        setCurrentPage(1);
        setSearchTerm(value);
      },
      [setCurrentPage, setSearchTerm]
    );

    const getFormLabelText = useCallback(() => {
      return selected && selected?.length > 0
        ? `Selected ${selected.length} selections.`
        : 'No selections found';
    }, [selected]);

    const renderText = useCallback(
      (text: string) => <Text size="sm">{text}</Text>,
      []
    );

    const renderFormLabel = useCallback(
      () => renderText(getFormLabelText()),
      [getFormLabelText, renderText]
    );

    return (
      <Box data-testid={`form-field-${field.name}`}>
        <Input.Wrapper
          label={renderFieldLabel(field)}
          withAsterisk={field.required}
          error={error}
          description={field.description}>
          {renderFormLabel()}
          <Space mb="xs" />
          <Button
            size="sm"
            variant="outline"
            color="black"
            mb="xs"
            disabled={field.disabled}
            onClick={() => setOpen(true)}>
            Choose selections
          </Button>
        </Input.Wrapper>

        <Modal
          opened={open}
          centered
          zIndex={9999}
          size="xl"
          onClose={handleCloseDialog}
          title="Select one or more items">
          <Modal.Header
            pos={'sticky'}
            style={{paddingLeft: 0, paddingRight: 0}}>
            <Box w="100%">
              <Group mt="md" justify="space-between">
                <CheckboxRow
                  label={`Select All`}
                  checked={hasSelectedAll}
                  onChange={handleSelectAll}
                />
                <CheckboxRow
                  label={`Show Only Selected`}
                  checked={showOnlySelected}
                  disabled={hasSelectedAll}
                  onChange={handleShowOnlySelected}
                />
              </Group>
              <Box mb="md">
                <Textbox
                  label="Search"
                  name="search"
                  value={searchTerm}
                  onChangeCallback={handleSearchTerm}
                />
              </Box>
              <Paginator
                mt={8}
                mb={16}
                currentPage={currentPage}
                totalRecords={totalRecords}
                onChange={handlePageChange}
                perPage={maxPageRows}
              />
            </Box>
          </Modal.Header>
          {isValidArray(displayedData) ? (
            displayedData.map(item => {
              const chunks = item.label.split(' | ');
              const label = chunks[0] ?? item.label;
              const helpText = chunks.slice(1, chunks.length).join(' | ');
              return (
                <CheckboxRow
                  key={item.id}
                  id={item.id.toString()}
                  label={label}
                  helpText={helpText}
                  checked={selected.includes(item.id.toString())}
                  onChange={e => handleSelect(e, item.id.toString())}
                />
              );
            })
          ) : (
            <Text mt="lg">No selections found</Text>
          )}

          <Modal.Header pos={'sticky'} bottom={0}>
            <Group justify="end" w="100%">
              <Button
                color="black"
                variant="subtle"
                size="sm"
                onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button onClick={handleSaveSelections} size="sm">
                Save Selections
              </Button>
            </Group>
          </Modal.Header>
        </Modal>
      </Box>
    );
  }
);
